import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '../hooks/useTranslation';
import styles from './HairstylistsMap.module.css';
import { getPrimaryCity } from '../utils/locationHelpers';
import { loadGoogleMaps } from '../utils/googleMapsLoader';

const BELGIUM_CENTER = { lat: 50.5039, lng: 4.4699 };
const DEFAULT_ZOOM = 7;
const CITY_FOCUS_ZOOM = 11;

/** Centre et zoom par pays (code en minuscules) pour recentrer la carte */
const COUNTRY_VIEWS = {
  be: { center: { lat: 50.5039, lng: 4.4699 }, zoom: 7 },
  fr: { center: { lat: 46.603354, lng: 1.888334 }, zoom: 6 },
  nl: { center: { lat: 52.1326, lng: 5.2913 }, zoom: 7 },
  lu: { center: { lat: 49.8153, lng: 6.1296 }, zoom: 9 },
  de: { center: { lat: 51.1657, lng: 10.4515 }, zoom: 6 },
  at: { center: { lat: 47.5162, lng: 14.5501 }, zoom: 7 },
  ch: { center: { lat: 46.8182, lng: 8.2275 }, zoom: 7 },
  gb: { center: { lat: 55.3781, lng: -3.4360 }, zoom: 6 },
  es: { center: { lat: 40.4637, lng: -3.7492 }, zoom: 6 },
  it: { center: { lat: 41.8719, lng: 12.5674 }, zoom: 6 },
  pt: { center: { lat: 39.3999, lng: -8.2245 }, zoom: 6 },
  no: { center: { lat: 60.4720, lng: 8.4689 }, zoom: 5 },
  se: { center: { lat: 60.1282, lng: 18.6435 }, zoom: 5 },
  dk: { center: { lat: 56.2639, lng: 9.5018 }, zoom: 7 },
  fi: { center: { lat: 61.9241, lng: 25.7482 }, zoom: 5 },
  cd: { center: { lat: -4.0383, lng: 21.7587 }, zoom: 5 },
  cg: { center: { lat: -0.2280, lng: 15.8277 }, zoom: 5 },
};

function HairstylistsMap({ hairstylists = [], selectedCityCoords = null, selectedCountry = 'be', onMarkerClick = null, providerType = 'hair' }) {
  const { t, language } = useTranslation();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [mapsReady, setMapsReady] = useState(!!window.google?.maps);
  const geocoderRef = useRef(null);
  const cityCoordsCacheRef = useRef({});
  const isInitialLoadRef = useRef(true);
  const lastSelectedCityCoordsRef = useRef(null);
  const lastSelectedCountryRef = useRef(selectedCountry);

  // Regrouper les coiffeuses par ville principale
  const cityGroups = useMemo(() => {
    const groups = {};
    hairstylists.forEach((stylist) => {
      const city = (getPrimaryCity(stylist) || '').trim();
      if (!city) return;
      if (!groups[city]) {
        groups[city] = [];
      }
      groups[city].push(stylist);
    });
    return Object.entries(groups).map(([city, stylists]) => ({ city, stylists }));
  }, [hairstylists]);

  // Initialiser Google Maps / Geocoder si besoin (langue = langue sélectionnée par l'utilisateur)
  useEffect(() => {
    loadGoogleMaps({ requirePlaces: false, language: language || 'fr' })
      .then(() => {
        if (window.google?.maps?.Geocoder && window.google?.maps?.Map && window.google?.maps?.Marker) {
          if (!geocoderRef.current) {
            geocoderRef.current = new window.google.maps.Geocoder();
          }
          setMapsReady(true);
        } else {
          // eslint-disable-next-line no-console
          console.error('Google Maps API incomplete');
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load Google Maps:', error);
      });
  }, [language]);

  // Fonction utilitaire pour géocoder une ville
  const geocodeCity = (city) =>
    new Promise((resolve) => {
      if (!city || !geocoderRef.current || !window.google?.maps?.Geocoder) {
        resolve(null);
        return;
      }

      if (cityCoordsCacheRef.current[city]) {
        resolve(cityCoordsCacheRef.current[city]);
        return;
      }

      try {
        geocoderRef.current.geocode({ address: city }, (results, status) => {
          if (status === 'OK' && results?.[0]?.geometry?.location) {
            const location = results[0].geometry.location;
            if (typeof location.lat === 'function' && typeof location.lng === 'function') {
              const coords = { lat: location.lat(), lng: location.lng() };
              cityCoordsCacheRef.current[city] = coords;
              resolve(coords);
            } else {
              resolve(null);
            }
          } else {
            resolve(null);
          }
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Geocoding error:', error);
        resolve(null);
      }
    });

  // Créer / mettre à jour la carte et les marqueurs
  useEffect(() => {
    if (!mapsReady || !mapRef.current || !window.google?.maps) {
      return;
    }

    const countryKey = (selectedCountry || 'be').toLowerCase();
    const countryView = COUNTRY_VIEWS[countryKey];

    if (!mapInstanceRef.current) {
      const initialCenter = countryView?.center || BELGIUM_CENTER;
      const initialZoom = countryView?.zoom ?? DEFAULT_ZOOM;
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: initialCenter,
        zoom: initialZoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        // Permettre le déplacement à un seul doigt sur mobile
        gestureHandling: 'greedy',
      });
    }

    const map = mapInstanceRef.current;

    // Nettoyer les anciens marqueurs
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    if (!cityGroups.length) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    // Créer une InfoWindow unique pour tous les marqueurs
    const infoWindow = new window.google.maps.InfoWindow({
      maxWidth: 320,
    });

    const createMarkers = async () => {
      // Vérifier que l'API Google Maps est bien chargée
      if (!window.google?.maps?.LatLng || !window.google?.maps?.Marker) {
        // eslint-disable-next-line no-console
        console.error('Google Maps API not fully loaded');
        return;
      }
      
      // Initialiser le geocoder si nécessaire
      if (!geocoderRef.current && window.google?.maps?.Geocoder) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }

      // Géocoder les villes en parallèle
      const geocodeResults = await Promise.all(
        cityGroups.map(async ({ city, stylists }) => {
          const coords = await geocodeCity(city);
          return { city, stylists, coords };
        }),
      );

      // Regrouper les villes très proches pour éviter plusieurs piquets quasi superposés
      const clustersByCoord = {};
      geocodeResults.forEach(({ city, stylists, coords }) => {
        if (!coords || typeof coords.lat !== 'number' || typeof coords.lng !== 'number') return;
        // Regrouper par coordonnées arrondies (~ à quelques centaines de mètres près)
        const key = `${coords.lat.toFixed(2)},${coords.lng.toFixed(2)}`;
        if (!clustersByCoord[key]) {
          clustersByCoord[key] = {
            coords,
            cities: [],
            stylists: [],
          };
        }
        clustersByCoord[key].cities.push(city);
        clustersByCoord[key].stylists.push(...stylists);
      });

      Object.values(clustersByCoord).forEach((cluster) => {
        const { coords, cities, stylists } = cluster;
        if (!coords || !stylists.length || typeof coords.lat !== 'number' || typeof coords.lng !== 'number') return;

        const position = new window.google.maps.LatLng(coords.lat, coords.lng);
        bounds.extend(position);

        // Créer une icône SVG mauve personnalisée pour le marqueur (30% plus clair, avec contour mauve)
        const markerIcon = {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#B097DD', // Mauve 30% plus clair que #8e6acf
          fillOpacity: 1,
          strokeColor: '#FFFFFF', // Contour de la même couleur mauve
          strokeWeight: 1.5,
          anchor: new window.google.maps.Point(0, 0),
        };

        const marker = new window.google.maps.Marker({
          position,
          map,
          icon: markerIcon,
          label: {
            text: String(stylists.length),
            color: '#ffffff',
            fontSize: '12px',
            fontWeight: '500',
          },
        });

        const uniqueCities = Array.from(new Set(cities));
        const mainCityName =
          uniqueCities.length === 1 ? uniqueCities[0] : `${uniqueCities[0]} + ${uniqueCities.length - 1} autres`;
        const cityTitle = `${mainCityName} (${stylists.length})`;

        const content = `
          <div style="max-width: 320px; font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
            <h3 style="margin: 0 0 8px; font-size: 0.95rem; font-weight: 600; color: #333;">${cityTitle}</h3>
            <ul style="list-style: none; padding: 0; margin: 0; max-height: 180px; overflow-y: auto;">
              ${stylists
                .map(
                  (stylist) => `
                <li style="display: flex; align-items: center; gap: 8px; padding: 8px 0; border-bottom: 1px solid rgba(0, 0, 0, 0.06);">
                  <img src="${stylist.profilePhoto || ''}" alt="${stylist.name || ''}" style="width: 32px; height: 32px; border-radius: 50%; object-fit: cover; background: #eee; flex-shrink: 0;" />
                  <div style="display: flex; flex-direction: column; gap: 2px;">
                    <div style="font-size: 0.9rem; font-weight: 500; color: #333;">${stylist.name || ''}</div>
                    <a href="/provider/${providerType}/${stylist.id}" style="font-size: 0.8rem; color: #8e6acf; text-decoration: none;">
                      ${t('card.viewProfile')}
                    </a>
                  </div>
                </li>
              `,
                )
                .join('')}
            </ul>
          </div>
        `;

        marker.addListener('click', () => {
          // Toujours afficher le popup InfoWindow
          infoWindow.setContent(content);
          infoWindow.open(map, marker);
          
          // Si onMarkerClick est fourni, afficher aussi la liste en dessous
          if (onMarkerClick) {
            onMarkerClick(stylists, cityTitle);
          }
        });

        markersRef.current.push(marker);
      });

      // Ne réinitialiser le zoom que si :
      // 1. C'est le premier chargement
      // 2. La ville sélectionnée a vraiment changé (selectedCityCoords non nul)
      const cityCoordsChanged =
        !!selectedCityCoords &&
        (
          !lastSelectedCityCoordsRef.current ||
          lastSelectedCityCoordsRef.current.lat !== selectedCityCoords.lat ||
          lastSelectedCityCoordsRef.current.lng !== selectedCityCoords.lng
        );

      const countryChanged = lastSelectedCountryRef.current !== (selectedCountry || 'be');
      if (countryChanged) lastSelectedCountryRef.current = (selectedCountry || 'be').toLowerCase();

      if (isInitialLoadRef.current || cityCoordsChanged || countryChanged) {
        if (selectedCityCoords) {
          // Cas: une ville est sélectionnée dans les filtres -> focus sur cette zone
          const target = new window.google.maps.LatLng(selectedCityCoords.lat, selectedCityCoords.lng);
          map.setCenter(target);
          map.setZoom(CITY_FOCUS_ZOOM);
          lastSelectedCityCoordsRef.current = selectedCityCoords;
        } else if (countryView && (isInitialLoadRef.current || countryChanged)) {
          // Cas: zoom initial ou changement de pays -> centrer et zoomer sur le pays sélectionné
          map.setCenter(countryView.center);
          map.setZoom(countryView.zoom);
          lastSelectedCityCoordsRef.current = null;
        } else if (isInitialLoadRef.current && !bounds.isEmpty()) {
          // Cas: premier chargement sans pays reconnu mais avec marqueurs -> fitBounds
          map.fitBounds(bounds);
          lastSelectedCityCoordsRef.current = null;
        } else if (isInitialLoadRef.current) {
          // Cas: aucun résultat / pays non reconnu -> centre par défaut
          map.setCenter(BELGIUM_CENTER);
          map.setZoom(DEFAULT_ZOOM);
          lastSelectedCityCoordsRef.current = null;
        }

        isInitialLoadRef.current = false;
      }
    };

    createMarkers();
  }, [mapsReady, cityGroups, t, selectedCityCoords, selectedCountry]);

  const handleZoom = (delta) => {
    if (!mapInstanceRef.current) return;
    const currentZoom = mapInstanceRef.current.getZoom() ?? DEFAULT_ZOOM;
    const nextZoom = Math.max(3, Math.min(18, currentZoom + delta));
    mapInstanceRef.current.setZoom(nextZoom);
  };

  if (!mapsReady) {
    return (
      <div className={styles.mapWrapper}>
        <div className={styles.mapContainer}>
        <div className={styles.mapLoading}>Google Maps est en cours de chargement…</div>
      </div>
      </div>
    );
  }

  return (
    <div className={styles.mapWrapper} data-map-container>
      <div ref={mapRef} className={styles.mapContainer} />
      <div className={styles.zoomControls}>
        <button
          type="button"
          className={styles.zoomButton}
          onClick={() => handleZoom(1)}
          aria-label="Zoomer"
        >
          +
        </button>
        <button
          type="button"
          className={styles.zoomButton}
          onClick={() => handleZoom(-1)}
          aria-label="Dézoomer"
        >
          −
        </button>
      </div>
    </div>
  );
}

HairstylistsMap.propTypes = {
  hairstylists: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedCityCoords: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  selectedCountry: PropTypes.string,
  onMarkerClick: PropTypes.func,
  providerType: PropTypes.string,
};

export default HairstylistsMap;


